<template>
  <b-modal
    hide-footer
    size="lg"
    title="Team Name Mapping"
    centered
    id="team-mapping"
    @shown="resetAndFetch"
    @hide="handleModalHide"
  >
    <div class="text-black pt-1 pb-2">
      <div class="d-flex justify-content-between mb-1">
        <b-form-input
          v-model="search"
          class="border"
          style="max-width: 300px"
          placeholder="Search by team name..."
          @input="handleSearch"
        />
        <b-button
          variant="primary"
          style="padding: 6px 10px; font-size: 12px"
          @click="handleShowAdd"
          >Add mapping</b-button
        >
      </div>

      <div v-if="list.length > 0" style="max-height: 800px; overflow-y: auto">
        <div
          v-for="item in list"
          :key="item.sportCasTeamMapId"
          class="d-flex align-items-center justify-content-between p-1 border-bot"
        >
          <div>
            <div style="font-weight: 600; font-size: 13px">{{ item.ssTeamName }}</div>
            <div class="text-muted" style="font-size: 12px">{{ item.scTeamName }}</div>
          </div>
          <div class="d-flex" style="gap: 6px">
            <feather-icon
              size="18"
              icon="TrashIcon"
              class="text-danger cursor-pointer"
              @click="showDeleteConfirm(item)"
            />
            <feather-icon
              size="18"
              icon="EditIcon"
              class="text-warning cursor-pointer"
              @click="handleShowEdit(item)"
            />
          </div>
        </div>
        <div v-if="hasMore" class="text-center mt-1 pb-1">
          <b-button
            variant="outline-primary"
            size="sm"
            :disabled="loading"
            @click="handleLoadMore"
          >
            <Spinner v-if="loading" />
            <span v-else>Load more</span>
          </b-button>
        </div>
      </div>
      <div v-else class="mt-3 text-center">
        <Spinner v-if="loading" />
        <span v-else>No items in list</span>
      </div>

      <!-- MODAL ADD/EDIT -->
      <b-modal
        ref="form-modal"
        :title="modalType === 'add' ? 'Add Team Mapping' : 'Edit Team Mapping'"
        centered
        cancel-variant="outline-secondary"
        hide-footer
        @hide="hideFormModal"
      >
        <div style="color: black" class="mb-2">
          <p class="m-0" style="padding-bottom: 5px">Team Name Sportcast</p>
          <b-form-input
            v-model="form.scTeamName"
            class="border"
            placeholder="Enter Sportcast team name..."
          />
        </div>
        <div style="color: black" class="mb-2">
          <p class="m-0" style="padding-bottom: 5px">Team Name Sporting Solutions</p>
          <b-form-input
            v-model="form.ssTeamName"
            class="border"
            placeholder="Enter Sporting Solutions team name..."
          />
        </div>
        <b-row>
          <b-col class="d-flex justify-content-end mt-2">
            <b-button
              variant="primary"
              class="mb-1 mb-sm-0"
              :disabled="saving"
              @click="handleSave"
            >
              <Spinner v-if="saving" />
              <template v-else>Save</template>
            </b-button>
          </b-col>
        </b-row>
      </b-modal>

      <!-- MODAL CONFIRM DELETE -->
      <b-modal
        ref="confirm-modal"
        title="Delete Team Mapping"
        centered
        cancel-variant="outline-secondary"
        :ok-disabled="saving"
        @ok="handleConfirmDelete"
      >
        <b-card-text style="color: black">
          Are you sure you want to delete mapping "{{ deleteItem && deleteItem.scTeamName }}"?
        </b-card-text>
        <template #modal-ok>
          <Spinner v-if="saving" />
          <span v-else>Submit</span>
        </template>
      </b-modal>
    </div>
  </b-modal>
</template>

<script>
import useToastHandler from "@/services/toastHandler";
import { BCol, BRow, BButton, BFormInput, BCardText } from "bootstrap-vue";
import { mapActions } from "vuex";
import Spinner from "@/@core/components/Spinner.vue";

export default {
  components: { BRow, BCol, BButton, BFormInput, BCardText, Spinner },
  data() {
    return {
      list: [],
      page: 1,
      search: "",
      hasMore: false,
      loading: false,
      saving: false,
      modalType: "add",
      form: { scTeamName: "", ssTeamName: "" },
      editItem: null,
      deleteItem: null,
      searchTimeout: null,
    };
  },
  methods: {
    ...mapActions({
      getTeamMapList: "sport/market/getTeamMapList",
      createTeamMap: "sport/market/createTeamMap",
      updateTeamMap: "sport/market/updateTeamMap",
      deleteTeamMap: "sport/market/deleteTeamMap",
    }),
    handleModalHide() {
      this.list = [];
      this.page = 1;
      this.search = "";
      this.hasMore = false;
    },
    resetAndFetch() {
      this.list = [];
      this.page = 1;
      this.hasMore = false;
      this.fetchList();
    },
    async fetchList(append = false) {
      this.loading = true;
      try {
        const data = await this.getTeamMapList({
          page: this.page,
          limit: 20,
          ...(this.search && { search: this.search }),
        });
        if (data && Array.isArray(data.data)) {
          this.list = append ? [...this.list, ...data.data] : data.data;
          const total = data.total ?? data.meta?.total ?? data.pagination?.total;
          this.hasMore =
            total !== undefined ? this.list.length < total : data.data.length === 20;
        }
      } finally {
        this.loading = false;
      }
    },
    handleLoadMore() {
      this.page += 1;
      this.fetchList(true);
    },
    handleSearch() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.page = 1;
        this.list = [];
        this.fetchList();
      }, 400);
    },
    handleShowAdd() {
      this.modalType = "add";
      this.form = { scTeamName: "", ssTeamName: "" };
      this.editItem = null;
      this.$refs["form-modal"].show();
    },
    handleShowEdit(item) {
      this.modalType = "edit";
      this.form = { scTeamName: item.scTeamName, ssTeamName: item.ssTeamName };
      this.editItem = item;
      this.$refs["form-modal"].show();
    },
    hideFormModal() {
      this.form = { scTeamName: "", ssTeamName: "" };
      this.editItem = null;
    },
    async handleSave() {
      if (!this.form.scTeamName)
        return this.showToastError(false, "Please enter Sportcast Team Name");
      if (!this.form.ssTeamName)
        return this.showToastError(false, "Please enter Sporting Solutions Team Name");

      this.saving = true;
      try {
        if (this.modalType === "add") {
          const res = await this.createTeamMap(this.form);
          if (res) {
            this.showToastSuccess(true, "Team mapping added successfully!");
            this.$refs["form-modal"].hide();
            this.resetAndFetch();
          }
        } else {
          const res = await this.updateTeamMap({
            id: this.editItem.sportCasTeamMapId,
            scTeamName: this.form.scTeamName,
            ssTeamName: this.form.ssTeamName,
          });
          if (res) {
            this.showToastSuccess(true, "Team mapping updated successfully!");
            this.$refs["form-modal"].hide();
            this.resetAndFetch();
          }
        }
      } finally {
        this.saving = false;
      }
    },
    showDeleteConfirm(item) {
      this.deleteItem = item;
      this.$refs["confirm-modal"].show();
    },
    async handleConfirmDelete(event) {
      event.preventDefault();
      this.saving = true;
      try {
        const res = await this.deleteTeamMap(this.deleteItem.sportCasTeamMapId);
        if (res) {
          this.showToastSuccess(true, "Team mapping deleted successfully!");
          this.$refs["confirm-modal"].hide();
          this.resetAndFetch();
        }
      } finally {
        this.saving = false;
      }
    },
  },
  setup() {
    const { showToastError, showToastSuccess } = useToastHandler();
    return { showToastError, showToastSuccess };
  },
};
</script>

<style lang="scss" scoped>
.border-bot {
  border-bottom: 1px solid #dae1e7;
}
</style>
